import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import adminGameService from '../../../utils/adminGameService';
import { studioService } from '../../../utils/studioService';

const GameEditor = ({ game, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    studio: '',
    category: '',
    contentRating: 'E',
    tags: [],
    price: '0',
    revenueShare: 70,
    featured: false,
    status: 'draft',
    releaseDate: '',
    tier: 'premium',
    systemRequirements: {
      minRam: '4',
      minStorage: '2',
      webGpuSupport: true
    },
    // WebGPU specific fields
    webgpu_url: '',
    webgpu_data_url: '',
    webgpu_wasm_url: '',
    webgpu_framework_url: '',
    webgpu_loader_url: '',
    webgpu_config: {},
    // GameDistribution specific field
    gamedistribution_url: '',
    game_type: 'webgpu',
    // Auto-chat settings
    auto_chat_enabled: false
  });

  const [mediaFiles, setMediaFiles] = useState({
    screenshots: [],
    trailer: '',
    thumbnail: ''
  });

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [studios, setStudios] = useState([]);
  const [loadingStudios, setLoadingStudios] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const fileInputRef = useRef(null);
  const directoryInputRef = useRef(null);
  const [isUpdatingGameFiles, setIsUpdatingGameFiles] = useState(false);
  const [gameFileUpdateProgress, setGameFileUpdateProgress] = useState(0);

  // Load studios and categories from database
  useEffect(() => {
    const loadStudios = async () => {
      setLoadingStudios(true);
      try {
        const { data, error } = await studioService.getAllStudios();
        if (error) {
          console.error('Error loading studios:', error);
          setStudios([]);
        } else {
          setStudios(data || []);
        }
      } catch (err) {
        console.error('Error loading studios:', err);
        setStudios([]);
      } finally {
        setLoadingStudios(false);
      }
    };

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const result = await adminGameService.getGameCategories();
        if (result.success) {
          // Transform categories to the format expected by the Select component
          const categoryOptions = result.data.map(category => ({
            value: category.name,
            label: category.name
          }));
          setCategories(categoryOptions);
        } else {
          console.error('Error loading categories:', result.error);
          // Fallback to hardcoded categories if database fetch fails
          setCategories([
            { value: 'Action', label: 'Action' },
            { value: 'Adventure', label: 'Adventure' },
            { value: 'Puzzle', label: 'Puzzle' },
            { value: 'Strategy', label: 'Strategy' },
            { value: 'Racing', label: 'Racing' },
            { value: 'Sports', label: 'Sports' },
            { value: 'Simulation', label: 'Simulation' },
            { value: 'RPG', label: 'RPG' }
          ]);
        }
      } catch (err) {
        console.error('Error loading categories:', err);
        // Fallback to hardcoded categories if there's an exception
        setCategories([
          { value: 'Action', label: 'Action' },
          { value: 'Adventure', label: 'Adventure' },
          { value: 'Puzzle', label: 'Puzzle' },
          { value: 'Strategy', label: 'Strategy' },
          { value: 'Racing', label: 'Racing' },
          { value: 'Sports', label: 'Sports' },
          { value: 'Simulation', label: 'Simulation' },
          { value: 'RPG', label: 'RPG' }
        ]);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadStudios();
    loadCategories();
  }, []);

  // Update form data when game prop changes
  useEffect(() => {
    if (game) {
      // Handle studio field - could be either studio_id or studio name
      let studioValue = '';
      if (game.studio_id) {
        studioValue = game.studio_id;
      } else if (game.studio && studios.length > 0) {
        // Check if game.studio is already a UUID (36 characters with dashes)
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(game.studio);
        
        if (isUUID) {
          // If it's already a UUID, check if it exists in our studios list
          const foundStudio = studios.find(s => s.id === game.studio);
          studioValue = foundStudio ? game.studio : '';
        } else {
          // If it's a name, try to find studio by name
          const foundStudio = studios.find(s => s.name === game.studio);
          studioValue = foundStudio ? foundStudio.id : '';
        }
      } else {
        studioValue = game.studio || '';
      }

      setFormData({
        title: game.title || '',
        description: game.description || '',
        studio: studioValue,
        category: game.category || '',
        contentRating: game.contentRating || 'E',
        tags: game.tags || [],
        price: game.price || '0',
        revenueShare: game.revenueShare || 70,
        featured: game.featured || false,
        status: game.status || 'draft',
        releaseDate: game.releaseDate || '',
        tier: game.tier || 'premium',
        systemRequirements: game.systemRequirements || {
          minRam: '4',
          minStorage: '2',
          webGpuSupport: true
        },
        // WebGPU specific fields
        webgpu_url: game.webgpu_url || '',
        webgpu_data_url: game.webgpu_data_url || '',
        webgpu_wasm_url: game.webgpu_wasm_url || '',
        webgpu_framework_url: game.webgpu_framework_url || '',
        webgpu_loader_url: game.webgpu_loader_url || '',
        webgpu_config: game.webgpu_config || {},
        // GameDistribution specific field
        gamedistribution_url: game.gamedistribution_url || '',
        game_type: game.game_type || 'webgpu',
        // Auto-chat settings
        auto_chat_enabled: game.auto_chat_enabled || false
      });

      setMediaFiles({
        screenshots: game.screenshots || [],
        trailer: game.trailer || '',
        thumbnail: game.thumbnail || ''
      });
    } else {
      // Reset form when no game is selected
      setFormData({
        title: '',
        description: '',
        studio: '',
        category: '',
        contentRating: 'E',
        tags: [],
        price: '0',
        revenueShare: 70,
        featured: false,
        status: 'draft',
        releaseDate: '',
        tier: 'premium',
        systemRequirements: {
          minRam: '4',
          minStorage: '2',
          webGpuSupport: true
        },
        webgpu_url: '',
        webgpu_data_url: '',
        webgpu_wasm_url: '',
        webgpu_framework_url: '',
        webgpu_loader_url: '',
        webgpu_config: {},
        gamedistribution_url: '',
        game_type: 'webgpu'
      });

      setMediaFiles({
        screenshots: [],
        trailer: '',
        thumbnail: ''
      });
    }
  }, [game, studios]);


  const tierOptions = [
    { value: 'free', label: 'Free' },
    { value: 'premium', label: 'Premium' }
  ];

  const contentRatings = [
    { value: 'E', label: 'Everyone' },
    { value: 'E10+', label: 'Everyone 10+' },
    { value: 'T', label: 'Teen' },
    { value: 'M', label: 'Mature 17+' },
    { value: 'AO', label: 'Adults Only 18+' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'published', label: 'Published' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const gameTypeOptions = [
    { value: 'webgpu', label: 'WebGPU (Unity)' },
    { value: 'react', label: 'React (HTML5)' },
    { value: 'html5', label: 'HTML5 (Plain)' },
    { value: 'gamedistribution', label: 'GameDistribution.com' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSystemRequirementChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      systemRequirements: {
        ...prev.systemRequirements,
        [field]: value
      }
    }));
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length || !game?.id) return;
    
    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      // Prepare images for upload
      const images = {};
      files.forEach((file, index) => {
        images[`screenshot_${Date.now()}_${index}`] = file;
      });
      
      setUploadProgress(30);
      
      // Upload images to Supabase
      const result = await adminGameService.uploadGameImages(game.id, images);
      
      setUploadProgress(80);
      
      if (result.success) {
        // Add uploaded images to screenshots with their URLs
        const newScreenshots = Object.entries(result.data).map(([key, url]) => ({
          id: key,
          url: url,
          name: files[Object.keys(result.data).indexOf(key)]?.name || 'Screenshot'
        }));
        
        setMediaFiles(prev => ({
          ...prev,
          screenshots: [...prev.screenshots, ...newScreenshots]
        }));
        
        setUploadProgress(100);
        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
        }, 500);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error uploading screenshots:', error);
      alert(`Failed to upload screenshots: ${error.message}`);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const removeScreenshot = (index) => {
    setMediaFiles(prev => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  };

  const setAsCoverImage = (imageUrl) => {
    setMediaFiles(prev => ({
      ...prev,
      thumbnail: imageUrl
    }));
    
    // Also update the form data
    setFormData(prev => ({
      ...prev,
      thumbnail: imageUrl
    }));
  };

  const handleGameFileUpdate = async (event) => {
    const fileList = Array.from(event.target.files);
    if (!fileList.length || !game?.id) return;
    
    setIsUpdatingGameFiles(true);
    setGameFileUpdateProgress(10);
    
    try {
      // CRITICAL: Do NOT spread File objects - they lose their prototype methods
      // File objects from directory input already have webkitRelativePath set correctly
      const files = fileList;
      
      // Validate that we have the required files for the game type
      const filePaths = files.map(f => f.webkitRelativePath);
      const hasIndexHtml = filePaths.some(path => path.endsWith('index.html'));
      
      if (!hasIndexHtml) {
        throw new Error('No index.html file found in the uploaded directory');
      }
      
      if (formData.game_type === 'react') {
        const hasAssets = filePaths.some(path => path.includes('assets/'));
        if (!hasAssets) {
          console.warn('No assets directory found - this may be okay for some React builds');
        }
      }
      
      // Create manifest
      const manifest = files.map(file => ({
        path: file.webkitRelativePath,
        size: file.size,
        type: file.type || 'application/octet-stream'
      }));
      
      setGameFileUpdateProgress(30);
      
      // Upload the new game files using the update method
      const uploadData = {
        files: files,
        manifest: manifest,
        structureType: 'directory',
        gameType: formData.game_type
      };
      
      const uploadResult = await adminGameService.updateGameFiles(game.id, uploadData);
      
      setGameFileUpdateProgress(80);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error);
      }
      
      // Update the game with new directory information
      const updateResult = await adminGameService.updateGame(game.id, {
        game_directory_path: uploadResult.data.directoryPath,
        game_subdirectory: uploadResult.data.gameSubdirectory || '',
        game_manifest: uploadResult.data.manifest,
        updated_at: new Date().toISOString()
      });
      
      if (!updateResult.success) {
        throw new Error(updateResult.error);
      }
      
      setGameFileUpdateProgress(100);
      
      // Show success message with details
      const successMessage = `Game files updated successfully!\n\nDetails:\n- Uploaded: ${uploadResult.data.uploadedFiles} files\n- Failed: ${uploadResult.data.failedFiles} files\n- Total: ${uploadResult.data.totalFiles} files`;
      
      if (uploadResult.data.failedFiles > 0) {
        console.warn('Some files failed to upload:', uploadResult.data.failedFilesList);
      }
      
      alert(successMessage);
      
      setTimeout(() => {
        setIsUpdatingGameFiles(false);
        setGameFileUpdateProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error('Error updating game files:', error);
      alert(`Failed to update game files: ${error.message}`);
      setIsUpdatingGameFiles(false);
      setGameFileUpdateProgress(0);
    }
  };

  const handleSave = async () => {
    if (!game?.id) {
      alert('Cannot save: No game selected');
      return;
    }

    setIsUploading(true);
    try {
      // For GameDistribution games, process the URL to get the embedded URL
      let processedGameDistributionUrl = formData.gamedistribution_url;
      if (formData.game_type === 'gamedistribution' && formData.gamedistribution_url) {
        try {
          const proxyUrl = `/api/gamedistribution-proxy?url=${encodeURIComponent(formData.gamedistribution_url)}&gameTitle=${encodeURIComponent(formData.title)}`;
          const proxyResponse = await fetch(proxyUrl);
          if (proxyResponse.ok) {
            const proxyData = await proxyResponse.json();
            processedGameDistributionUrl = proxyData.embeddedUrl;
            console.log('Processed GameDistribution URL in editor:', processedGameDistributionUrl);
          } else {
            console.warn('Failed to process GameDistribution URL in editor, using original URL');
          }
        } catch (error) {
          console.warn('Error processing GameDistribution URL in editor:', error);
          // Continue with original URL if processing fails
        }
      }

      // Prepare screenshots array for database storage
      const screenshotUrls = mediaFiles.screenshots
        .filter(screenshot => screenshot.url) // Only include screenshots with URLs
        .map(screenshot => ({
          url: screenshot.url,
          caption: screenshot.name || `Screenshot`
        }));

      const gameData = {
        ...formData,
        id: game.id,
        thumbnail: mediaFiles.thumbnail || formData.thumbnail || game.thumbnail,
        cover_image_url: mediaFiles.thumbnail || formData.thumbnail || game.thumbnail,
        screenshots: screenshotUrls,
        gamedistribution_url: processedGameDistributionUrl, // Use processed URL
        lastModified: new Date().toISOString()
      };

      onSave(gameData);
    } catch (error) {
      console.error('Error saving game:', error);
      alert('Failed to save game');
    } finally {
      setIsUploading(false);
    }
  };

  if (!game && !formData.title) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="Gamepad2" size={64} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">Select a game to edit</h3>
          <p className="text-muted-foreground">
            Choose a game from the library or upload a new one
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {game ? 'Edit Game' : 'New Game'}
            </h2>
            <p className="text-muted-foreground">
              {game ? `Editing ${game.title}` : 'Upload and configure a new game'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              iconName="Save"
              loading={isUploading}
              disabled={isUploading}
            >
              {isUploading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Basic Information */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Game Title"
                type="text"
                placeholder="Enter game title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
              <Select
                label="Studio"
                placeholder={loadingStudios ? "Loading studios..." : "Select studio"}
                options={studios.map(studio => ({
                  value: studio.id,
                  label: studio.name
                }))}
                value={loadingStudios ? '' : formData.studio}
                onChange={(value) => handleInputChange('studio', value)}
                required
                disabled={loadingStudios}
                loading={loadingStudios}
              />
              <Select
                label="Category"
                placeholder={loadingCategories ? "Loading categories..." : "Select category"}
                options={categories}
                value={loadingCategories ? '' : formData.category}
                onChange={(value) => handleInputChange('category', value)}
                required
                disabled={loadingCategories}
                loading={loadingCategories}
              />
              <Select
                label="Content Rating"
                placeholder="Select rating"
                options={contentRatings}
                value={formData.contentRating}
                onChange={(value) => handleInputChange('contentRating', value)}
                required
              />
              <div>
                <Select
                  label="Game Type"
                  placeholder="Select game type"
                  options={gameTypeOptions}
                  value={formData.game_type}
                  onChange={(value) => handleInputChange('game_type', value)}
                  required
                />
                {formData.game_type !== game?.game_type && (
                  <p className="text-sm text-orange-600 mt-1">
                    ⚠️ Game type changed - configuration sections will update when you save
                  </p>
                )}
              </div>
              <Select
                label="Tier"
                placeholder="Select tier"
                options={tierOptions}
                value={formData.tier}
                onChange={(value) => handleInputChange('tier', value)}
                required
              />
              <Input
                label="Price (USD)"
                type="number"
                placeholder="0.00"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                disabled={formData.tier === 'free'}
              />
              <Input
                label="Revenue Share (%)"
                type="number"
                placeholder="70"
                min="0"
                max="100"
                value={formData.revenueShare}
                onChange={(e) => handleInputChange('revenueShare', e.target.value)}
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                className="w-full h-32 px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Enter game description..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </div>
          </div>

          {/* WebGPU Configuration */}
          {formData.game_type === 'webgpu' && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">WebGPU Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Data File URL"
                  type="url"
                  placeholder="https://..."
                  value={formData.webgpu_data_url}
                  onChange={(e) => handleInputChange('webgpu_data_url', e.target.value)}
                />
                <Input
                  label="WASM File URL"
                  type="url"
                  placeholder="https://..."
                  value={formData.webgpu_wasm_url}
                  onChange={(e) => handleInputChange('webgpu_wasm_url', e.target.value)}
                />
                <Input
                  label="Framework File URL"
                  type="url"
                  placeholder="https://..."
                  value={formData.webgpu_framework_url}
                  onChange={(e) => handleInputChange('webgpu_framework_url', e.target.value)}
                />
                <Input
                  label="Loader File URL"
                  type="url"
                  placeholder="https://..."
                  value={formData.webgpu_loader_url}
                  onChange={(e) => handleInputChange('webgpu_loader_url', e.target.value)}
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  WebGPU Configuration (JSON)
                </label>
                <textarea
                  className="w-full h-24 px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none font-mono text-sm"
                  placeholder='{"companyName": "Studio", "productName": "game"}'
                  value={JSON.stringify(formData.webgpu_config, null, 2)}
                  onChange={(e) => {
                    try {
                      const config = JSON.parse(e.target.value);
                      handleInputChange('webgpu_config', config);
                    } catch (err) {
                      // Invalid JSON, don't update
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* GameDistribution Configuration */}
          {formData.game_type === 'gamedistribution' && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">GameDistribution Configuration</h3>
              <div className="space-y-4">
                <Input
                  label="GameDistribution Game URL"
                  type="url"
                  placeholder="https://gamedistribution.com/games/game-name/"
                  value={formData.gamedistribution_url}
                  onChange={(e) => handleInputChange('gamedistribution_url', e.target.value)}
                  required
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-2">How to get the GameDistribution URL:</p>
                      <ol className="list-decimal list-inside space-y-1 text-blue-700">
                        <li>Go to the game page on GameDistribution.com</li>
                        <li>Copy the full URL from your browser's address bar</li>
                        <li>Paste it here - we'll automatically handle the embedding logic</li>
                      </ol>
                      <p className="mt-2 text-xs text-blue-600">
                        Example: https://gamedistribution.com/games/labubu-merge/
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Ad Notice</p>
                      <p className="text-yellow-700">
                        Games from GameDistribution.com may contain advertisements. 
                        An "ads" indicator will automatically be shown to users.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Media Management */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Media Assets</h3>
            
            {/* File Upload */}
            <div className="mb-6">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h4 className="text-lg font-medium text-foreground mb-2">Upload Screenshots</h4>
                <p className="text-muted-foreground">
                  Drag and drop files here or click to browse
                </p>
              </div>
              
              {isUploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-foreground">Uploading...</span>
                    <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Screenshots Grid */}
            {mediaFiles.screenshots.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {mediaFiles.screenshots.map((screenshot, index) => (
                  <div key={index} className="relative group">
                    <div className={`aspect-video rounded-lg overflow-hidden bg-muted ${
                      mediaFiles.thumbnail === (screenshot.url || screenshot) ? 'ring-2 ring-primary' : ''
                    }`}>
                      <Image
                        src={screenshot.url || screenshot}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        iconName="Image"
                        onClick={() => setAsCoverImage(screenshot.url || screenshot)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        title="Set as cover image"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        iconName="X"
                        onClick={() => removeScreenshot(index)}
                        title="Remove screenshot"
                      />
                    </div>
                    {mediaFiles.thumbnail === (screenshot.url || screenshot) && (
                      <div className="absolute bottom-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                        Cover Image
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Game File Management - Only show for directory-based games */}
          {game && game.game_structure_type === 'directory' && (
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Game File Management</h3>
              <p className="text-muted-foreground mb-4">
                Update the game files for this {formData.game_type === 'react' ? 'React' : 'WebGPU'} game. 
                This will replace all existing game files with the new version.
              </p>
              
              <div className="mb-6">
                <input
                  ref={directoryInputRef}
                  type="file"
                  webkitdirectory="true"
                  directory="true"
                  multiple
                  onChange={handleGameFileUpdate}
                  className="hidden"
                />
                <div
                  className="border-2 border-dashed border-orange-200 bg-orange-50 rounded-lg p-8 text-center cursor-pointer hover:border-orange-300 transition-colors"
                  onClick={() => directoryInputRef.current?.click()}
                >
                  <Icon name="RefreshCw" size={48} className="text-orange-600 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-orange-800 mb-2">Update Game Files</h4>
                  <p className="text-orange-700 mb-2">
                    {formData.game_type === 'react' 
                      ? 'Select the built React game folder (containing index.html and assets/)'
                      : 'Select the Unity WebGPU build folder (containing index.html, Build/, TemplateData/)'
                    }
                  </p>
                  <p className="text-sm text-orange-600">
                    ⚠️ This will replace all existing game files
                  </p>
                </div>
                
                {isUpdatingGameFiles && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-foreground">Updating game files...</span>
                      <span className="text-sm text-muted-foreground">{gameFileUpdateProgress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${gameFileUpdateProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {game.game_manifest && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <h5 className="font-medium text-foreground mb-2">Current Game Info:</h5>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Total Files:</strong> {game.game_manifest.totalFiles || 'Unknown'}</p>
                    <p><strong>Entry Point:</strong> {game.game_manifest.entryPoint || 'index.html'}</p>
                    <p><strong>Last Updated:</strong> {game.game_manifest.detectedAt ? new Date(game.game_manifest.detectedAt).toLocaleString() : 'Unknown'}</p>
                    {game.game_manifest.gameSubdirectory && (
                      <p><strong>Subdirectory:</strong> {game.game_manifest.gameSubdirectory}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* System Requirements */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">System Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Minimum RAM (GB)"
                type="number"
                placeholder="4"
                value={formData.systemRequirements.minRam}
                onChange={(e) => handleSystemRequirementChange('minRam', e.target.value)}
              />
              <Input
                label="Minimum Storage (GB)"
                type="number"
                placeholder="2"
                value={formData.systemRequirements.minStorage}
                onChange={(e) => handleSystemRequirementChange('minStorage', e.target.value)}
              />
            </div>
            <div className="mt-4">
              <Checkbox
                label="WebGPU Support Required"
                checked={formData.systemRequirements.webGpuSupport}
                onChange={(e) => handleSystemRequirementChange('webGpuSupport', e.target.checked)}
              />
            </div>
          </div>

          {/* Publishing Settings */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Publishing Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Status"
                options={statusOptions}
                value={formData.status}
                onChange={(value) => handleInputChange('status', value)}
              />
              <Input
                label="Release Date"
                type="date"
                value={formData.releaseDate}
                onChange={(e) => handleInputChange('releaseDate', e.target.value)}
              />
            </div>
            <div className="mt-4">
              <Checkbox
                label="Featured Game (Show in Hero Banner)"
                checked={formData.featured}
                onChange={(e) => handleInputChange('featured', e.target.checked)}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Featured games will appear in the hero banner on the main page
              </p>
            </div>
            <div className="mt-4">
              <Checkbox
                label="Enable Auto-Chat"
                checked={formData.auto_chat_enabled}
                onChange={(e) => handleInputChange('auto_chat_enabled', e.target.checked)}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Automatically show chat overlay in game. Players can interact with a game lobby chat without any developer code.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameEditor;
